import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Btn, Field, NativeSelect } from "@/components/ui/page-components";
import { useLoadTest, useRunLoadTest, usePurgeLoadTest } from "@/hooks/useTenants";
import type {
  Tenant,
  LoadTestMode,
  BookingLoadTestRun,
} from "@/types";
import { toast } from "sonner";

interface AxiosLikeError {
  response?: { data?: { detail?: string } };
}

function errMessage(e: unknown, fallback: string): string {
  return (e as AxiosLikeError)?.response?.data?.detail || fallback;
}

/** Default the date range to [today, today+7]. */
function defaultRange(): { from: string; to: string } {
  const today = new Date();
  const to = new Date(today);
  to.setDate(to.getDate() + 7);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(today), to: fmt(to) };
}

function StatBox({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const toneClass = {
    default: "text-foreground",
    good: "text-emerald-600",
    warn: "text-amber-600",
    bad: "text-red-600",
  }[tone];
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function LoadTestTab({ tenant }: { tenant: Tenant }) {
  const range = defaultRange();
  const [mode, setMode] = useState<LoadTestMode>("spread");
  const [count, setCount] = useState("50");
  const [dateFrom, setDateFrom] = useState(range.from);
  const [dateTo, setDateTo] = useState(range.to);
  const [confirmName, setConfirmName] = useState("");
  const [lastRun, setLastRun] = useState<BookingLoadTestRun | null>(null);

  const { data: overview, isLoading } = useLoadTest(tenant.id);
  const runMutation = useRunLoadTest();
  const purgeMutation = usePurgeLoadTest();

  const isActive = tenant.status === "active";
  const confirmOk = !isActive || confirmName.trim() === tenant.name;
  const countNum = Number(count);
  const countValid = Number.isFinite(countNum) && countNum >= 1 && countNum <= 1000;
  const canRun = countValid && !!dateFrom && !!dateTo && confirmOk && !runMutation.isPending;

  async function handleRun() {
    try {
      const run = await runMutation.mutateAsync({
        tenantId: tenant.id,
        params: {
          mode,
          count: countNum,
          date_from: dateFrom,
          date_to: dateTo,
          ...(isActive ? { confirm_name: confirmName.trim() } : {}),
        },
      });
      setLastRun(run);
      const blocked = run.blocked_overlap + run.blocked_unique;
      toast.success(
        `Run complete: ${run.created} created, ${blocked} blocked (${run.duration_ms} ms)`,
      );
    } catch (e) {
      toast.error(errMessage(e, "Load test failed"));
    }
  }

  async function handlePurge() {
    const live = overview?.live_test_bookings ?? 0;
    if (!window.confirm(`Delete ${live} test booking(s) for ${tenant.name}? This cannot be undone.`))
      return;
    try {
      const res = await purgeMutation.mutateAsync(tenant.id);
      toast.success(`Deleted ${res.deleted} test booking(s)`);
    } catch (e) {
      toast.error(errMessage(e, "Purge failed"));
    }
  }

  const live = overview?.live_test_bookings;
  const runs = overview?.runs ?? [];

  return (
    <div className="flex flex-col gap-5">
      {/* Controls */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-[15px] font-semibold text-foreground">
              Booking Load Tester
            </h3>
            <p className="text-[12px] text-muted-foreground">
              Generate test bookings to exercise the booking flow and duplicate
              prevention. All rows are tagged{" "}
              <code className="rounded bg-muted px-1">LOAD_TEST</code> and can be
              purged without touching real bookings.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Live test bookings
            </div>
            <div className="text-xl font-semibold text-foreground">
              {live == null ? "—" : live}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Field label="Mode" hint={mode === "spread" ? "Fill unique slots" : "Force duplicates"}>
            <NativeSelect value={mode} onChange={(v) => setMode(v as LoadTestMode)}>
              <option value="spread">Spread (unique slots)</option>
              <option value="collide">Collide (force duplicates)</option>
            </NativeSelect>
          </Field>
          <Field label="Count (1–1000)">
            <Input
              type="number"
              min={1}
              max={1000}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </Field>
          <Field label="Date from">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Field>
          <Field label="Date to">
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Field>
        </div>

        {isActive && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-[13px] font-semibold text-amber-800">
              ⚠ {tenant.name} is an active (production) tenant.
            </p>
            <p className="mb-2 text-[12px] text-amber-700">
              Type the tenant name to confirm you want to write test bookings to
              its live database.
            </p>
            <Input
              placeholder={tenant.name}
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="max-w-xs"
            />
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Btn onClick={handleRun} disabled={!canRun} icon="zap">
            {runMutation.isPending ? "Running…" : "Run load test"}
          </Btn>
          <Btn
            variant="destructive"
            onClick={handlePurge}
            disabled={!live || purgeMutation.isPending}
            icon="trash"
          >
            {purgeMutation.isPending ? "Purging…" : `Purge test bookings${live ? ` (${live})` : ""}`}
          </Btn>
          {!countValid && (
            <span className="text-[12px] text-red-600">Count must be 1–1000.</span>
          )}
        </div>
      </Card>

      {/* Last run result */}
      {lastRun && (
        <Card className="p-5">
          <h4 className="mb-3 text-[13px] font-semibold text-foreground">
            Last run — {lastRun.mode}, requested {lastRun.requested}
          </h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
            <StatBox label="Attempted" value={lastRun.attempted} />
            <StatBox label="Created" value={lastRun.created} tone="good" />
            <StatBox label="Blocked (overlap)" value={lastRun.blocked_overlap} tone="warn" />
            <StatBox label="Blocked (unique)" value={lastRun.blocked_unique} tone="warn" />
            <StatBox
              label="Other errors"
              value={lastRun.other_errors}
              tone={lastRun.other_errors ? "bad" : "default"}
            />
            <StatBox label="Duration" value={`${lastRun.duration_ms} ms`} />
          </div>
          {!!lastRun.summary?.notes?.length && (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-[12px] text-muted-foreground">
              {lastRun.summary.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}
          {!!lastRun.summary?.error_samples?.length && (
            <details className="mt-2 text-[12px] text-muted-foreground">
              <summary className="cursor-pointer">Error samples</summary>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {lastRun.summary.error_samples.map((s, i) => (
                  <li key={i} className="font-mono">
                    {s}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}

      {/* Run history */}
      <Card className="p-5">
        <h4 className="mb-3 text-[13px] font-semibold text-foreground">
          Run history
        </h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Blocked</TableHead>
              <TableHead>Errors</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground">
                  No runs yet.
                </TableCell>
              </TableRow>
            ) : (
              runs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell>{r.mode}</TableCell>
                  <TableCell>{r.requested}</TableCell>
                  <TableCell className="text-emerald-600">{r.created}</TableCell>
                  <TableCell className="text-amber-600">
                    {r.blocked_overlap + r.blocked_unique}
                  </TableCell>
                  <TableCell className={r.other_errors ? "text-red-600" : ""}>
                    {r.other_errors}
                  </TableCell>
                  <TableCell>{r.duration_ms} ms</TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.created_by || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
